import { Button } from '@/global/components/Button'
import Skeleton from '@/global/components/Skeleton'
import Grid from '@/global/components/ui/Grid'
import { FormField, FormFieldAutocomplete } from 'global/components/Form'
import type { IMapHandlerProps } from 'global/components/Map'
import { useDebouncedValue } from 'hooks'
import { MapPinPlusIcon } from 'lucide-react'
import { lazy, Suspense, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
    type Coordenadas,
    type Endereco,
    local,
    type SugestaoEndereco,
} from 'service/api'
import { toast } from 'sonner'
import {
    DialogEnderecoManual,
    type EnderecoManual,
} from './DialogEnderecoManual'

const MapComponent = lazy(() =>
    import('global/components/Map').then(m => ({ default: m.MapComponent })),
)

type Position = { lat: number; lng: number }

type FormProps = {
    latitude: number | string
    longitude: number | string
    cercaEletronica: number
    endereco: string
}

export const LocalForm = () => {
    const mapRef = useRef<IMapHandlerProps>(null)
    const { setValue, watch, getValues } = useFormContext<FormProps>()
    const { cercaEletronica, latitude, longitude } = watch()

    const [busca, setBusca] = useState('')
    const [manual, setManual] = useState(false)
    const buscaAtrasada = useDebouncedValue(busca.trim(), 300)

    // Erro aqui é Google fora; lista vazia é só busca sem resultado.
    const { data: sugestoes = [], isFetching: buscando } =
        local.sugestoes.useFetch<SugestaoEndereco[]>(false, {
            queryParams: { busca: buscaAtrasada },
            enabled: buscaAtrasada.length > 2,
            retry: false,
            onError: () => setManual(true),
        })

    const aplicarPosicao = ({ lat, lng }: Position) => {
        mapRef.current?.setMapPosition({ lat, lng })
        mapRef.current?.setMapZoom(16)
        setValue('latitude', lat)
        setValue('longitude', lng)
    }

    const handleSetCircleMarker = (ev: any) => {
        mapRef.current?.setMarkerCircle(+ev.target.value)
    }

    const handleGetMapPosition = async ({ lat, lng }: Position) => {
        setValue('latitude', lat)
        setValue('longitude', lng)

        const achado = await local.endereco
            .get<Endereco>({ latitude: lat, longitude: lng })
            .catch(() => null)
        if (achado)
            setValue('endereco', achado.endereco, {
                shouldDirty: true,
                shouldTouch: true,
            })
    }

    const handleSelecionarEndereco = async (endereco: string | null) => {
        if (!endereco) return

        const achado = await local.coordenadas
            .get<Coordenadas>({ endereco })
            .catch(() => null)
        if (achado) {
            aplicarPosicao({ lat: achado.latitude, lng: achado.longitude })
            return
        }

        toast.error('Não encontramos as coordenadas desse endereço')
        setManual(true)
    }

    const handleConfirmarManual = (valores: EnderecoManual) => {
        setValue('endereco', valores.endereco, { shouldDirty: true })
        aplicarPosicao({ lat: +valores.latitude, lng: +valores.longitude })
    }

    return (
        <Grid container>
            <Grid item xs={8}>
                <FormFieldAutocomplete
                    name="endereco"
                    label="Endereço"
                    options={sugestoes.map(item => item.descricao)}
                    loading={buscando}
                    paraCampo={endereco => endereco || null}
                    paraFormulario={endereco => endereco ?? ''}
                    inputValue={busca}
                    onInputChange={setBusca}
                    onChange={handleSelecionarEndereco}
                    renderOption={endereco => (
                        <span className="flex items-center gap-1">
                            <MapPinPlusIcon className="shrink-0 text-destructive" />
                            {endereco}
                        </span>
                    )}
                    noOptionsText={
                        buscaAtrasada.length > 2
                            ? 'Nenhum endereço encontrado'
                            : 'Digite ao menos 3 letras do endereço'
                    }
                    required
                />
            </Grid>
            <Grid item xs={4}>
                <FormField
                    name="cercaEletronica"
                    label="Cerca eletrônica"
                    onChange={handleSetCircleMarker}
                    required
                    endAdornment={
                        <span className="text-sm text-muted-foreground">m</span>
                    }
                />
            </Grid>
            <Grid item xs={12} className="flex justify-end">
                <Button variant="text" onClick={() => setManual(true)}>
                    Informar manualmente
                </Button>
            </Grid>
            <Grid item xs={12}>
                <Suspense fallback={<Skeleton className="h-96" />}>
                    <MapComponent
                        height="24rem"
                        getMapPosition={handleGetMapPosition}
                        ref={mapRef}
                        lat={+latitude}
                        lng={+longitude}
                        radius={+cercaEletronica}
                    />
                </Suspense>
            </Grid>
            {manual && (
                <DialogEnderecoManual
                    inicial={{
                        endereco: getValues('endereco') ?? '',
                        latitude: latitude ? String(latitude) : '',
                        longitude: longitude ? String(longitude) : '',
                    }}
                    onClose={() => setManual(false)}
                    onConfirm={handleConfirmarManual}
                />
            )}
        </Grid>
    )
}
